import { DateTime } from 'luxon';

import type { Item } from '@/models';
import { FuelEntityType, FuelPortalService } from '@/services';
import FuelAnalyticsService from '@/services/fuel-analytics-service';
import clientUtils from '@/utils/clientUtils';

import type { AppAnalyticsState, BaseAnalyticsData } from './AppAnalytics';
import type { AaExecuteSyncBatchItem, AaQueryResult, DashboardBatchResults } from './appAnalyticsBatchTypes';
import { DASHBOARD_BATCH_QUERY_KEYS } from './appAnalyticsBatchTypes';
import { SnowflakeCache } from './SnowflakeCache';

const cacheUtils = SnowflakeCache();

export enum SnowflakeQueries {
  topChannels = 'topChannels',
  content = 'content',
  contentByTop = 'contentByTop',
  channel = 'channel',
  liveChannel = 'liveChannel',
  pageViews = 'pageViews',
  uniqueVisitors = 'uniqueVisitors',
  videoViews = 'videoViews',
  videoByDevice = 'videoByDevice',
  videoByCountry = 'videoByCountry',
  /** @deprecated Use per-chart queries instead */
  unified = 'unified',
}

const AppAnalyticsSnowflakeService = () => {
  const FN_MAP = {
    topChannels: fetchTopChannels,
    content: fetchContentAnalytics,
    contentByTop: fetchTopContentData,
    channel: fetchChannelAnalytics,
    liveChannel: fetchLiveChannelAnalytics,
    pageViews: fetchPageViews,
    uniqueVisitors: fetchUniqueVisitors,
    videoViews: fetchVideoViews,
    videoByDevice: fetchVideoByDevice,
    videoByCountry: fetchVideoByCountry,
    unified: fetchUnifiedAnalytics,
  };

  const getData = async (type: SnowflakeQueries, params: AppAnalyticsState) => {
    const handler = FN_MAP[type];

    if (!handler) {
      throw new Error(`No handler found for type: ${type}`);
    }

    const key = _composeKey(type, params);
    const cached = cacheUtils.get(key);
    if (cached) {
      if (cached === 'loading') {
        return _retryInflightRequest(type, params, 100);
      }
      return cached;
    }

    cacheUtils.request(key);
    const data = await handler(params);
    cacheUtils.cache(key, data);

    return data;
  };

  return {
    getData,
  };
};

export { AppAnalyticsSnowflakeService };

export function buildDashboardFetchKey(params: AppAnalyticsState): string {
  return [
    params.fromDate,
    params.toDate,
    params.sites?.slice().sort().join(','),
    params.selectedItems.map((x) => x.id).join(','),
    params.selectedChannels.map((x) => x.id).join(','),
    (params.categoryVideoIds ?? []).slice().sort().join(','),
    params.groupBy,
    params.timePart,
    params.offset,
  ].join('|');
}

export function buildDashboardBatchQueries(params: AppAnalyticsState): AaExecuteSyncBatchItem[] {
  return DASHBOARD_BATCH_QUERY_KEYS.map((id) => ({
    id,
    queryString: _buildQueryByType(id, params),
  }));
}

export async function executeDashboardBatch(params: AppAnalyticsState): Promise<DashboardBatchResults> {
  const batchResponse = await FuelAnalyticsService.executeSyncBatch({
    appName: 'FuelTools',
    provider: 'SnowflakeLegacy',
    queries: buildDashboardBatchQueries(params),
  });

  const results: DashboardBatchResults = {};

  for (const key of DASHBOARD_BATCH_QUERY_KEYS) {
    results[key] = await _postProcessBatchRows(key, _extractBatchRows(batchResponse.results?.[key]), params);
  }

  return results;
}

// #region Query builders

function buildTopChannelsQuery(params: AppAnalyticsState): string {
  return `
            SELECT 
              "site",
              COALESCE("liveChannel", "channel") as "channel",
              count(*) AS "count",
              to_number(sum("params":content.duration), 29, 9) AS "duration",
              (
              CASE "count"
                WHEN 0
                THEN 0
                ELSE to_number("duration" / "count", 29, 9)
              END
              ) AS "average"
            FROM EVENTS
            WHERE "eventtype" IN ('watch', 'liveChannelWatch')
              AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
              AND "site" IN ('${params.sites.join("','")}')
              AND COALESCE("liveChannel", "channel") IS NOT NULL
            GROUP BY "site", COALESCE("liveChannel", "channel")
            ORDER BY "count" DESC
              LIMIT 10;`;
}

function buildContentAnalyticsQuery(params: AppAnalyticsState): string {
  let query = `
      SELECT "video",
        ANY_VALUE(coalesce("extras":title, "extras":video:title)) as "title",
        count(*) AS "count",
        to_number(sum("params": content.duration), 29, 9) AS "duration",
        CASE "count" WHEN 0 THEN 0 ELSE to_number("duration" / "count", 29, 9) END AS "average"
      FROM EVENTS
      WHERE "site" IN(${params.sites.map((site) => `'${site}'`).join(', ')})
        AND "eventtype" = 'watch'
        AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
        AND "video" IS NOT NULL`;

  query += _getVideoWhereClause(params);
  query += `
      GROUP BY "video"
      ORDER BY "count" desc;`;
  return query;
}

function buildChannelAnalyticsQuery(params: AppAnalyticsState): string {
  let query = `
      SELECT COALESCE("liveChannel", "channel") as "channel",
        count(*) AS "count",
        to_number(sum("params": content.duration), 29, 9) AS "duration",
        CASE "count" WHEN 0 THEN 0 ELSE to_number("duration" / "count", 29, 9) END AS "average",
        MAX(CASE WHEN "liveChannel" IS NOT NULL THEN 1 ELSE 0 END) AS "isLive"
      FROM EVENTS
      WHERE "site" IN(${params.sites.map((site) => `'${site}'`).join(', ')})
        AND "eventtype" IN ('watch', 'liveChannelWatch')
        AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
        AND COALESCE("liveChannel", "channel") IS NOT NULL`;

  query += _getChannelWhereClause(params);
  query += `
      GROUP BY COALESCE("liveChannel", "channel")
      ORDER BY "count" desc;`;
  return query;
}

function buildLiveChannelAnalyticsQuery(params: AppAnalyticsState): string {
  let query = `
      SELECT "liveChannel" as "channel",
        count(*) AS "count",
        to_number(sum("params": content.duration), 29, 9) AS "duration",
        CASE "count" WHEN 0 THEN 0 ELSE to_number("duration" / "count", 29, 9) END AS "average"
      FROM EVENTS
      WHERE "site" IN(${params.sites.map((site) => `'${site}'`).join(', ')})
        AND "eventtype" = 'liveChannelWatch'
        AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
        AND "liveChannel" IS NOT NULL`;

  query += _getChannelWhereClause(params);
  query += `
      GROUP BY "liveChannel"
      ORDER BY "count" desc;`;
  return query;
}

function buildPageViewsQuery(params: AppAnalyticsState): string {
  let eventTypes = ['page'];
  if (params.selectedChannels.length > 0) {
    eventTypes = ['video', 'liveChannel'];
  } else if (params.selectedItems.length > 0) {
    eventTypes = ['video'];
  }

  let query = `SELECT
            ${_getGroupByClause(params)} AS "site",
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")) AS "eventdate",
            COUNT(*) AS total
          FROM EVENTS
          WHERE "eventtype" IN ('${eventTypes.join("','")}')
            AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
            AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);
  query += `
          GROUP BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
          ORDER BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;
  return query;
}

function buildTopContentDataQuery(params: AppAnalyticsState): string {
  let query = `SELECT
              "site",
              "video",
              ANY_VALUE(coalesce("extras":title, "extras":video:title)) as "title",
              LISTAGG(DISTINCT COALESCE("channel", 'NULL'), ',') WITHIN GROUP (ORDER BY COALESCE("channel", 'NULL')) AS "channels",
              count(*) AS "count",
              to_number(sum("params":content.duration), 29, 9) AS "duration",
              (
              CASE "count"
                WHEN 0
                THEN 0
                ELSE to_number("duration" / "count", 29, 9)
              END
              ) AS "average"
            FROM EVENTS
            WHERE "eventtype" = 'watch'
              AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
              AND "site" IN ('${params.sites.join("','")}')
              AND "video" IS NOT NULL`;

  query += `
            GROUP BY "site", "video"
            ORDER BY "count" DESC
              LIMIT 10;`;
  return query;
}

function buildUniqueVisitorsQuery(params: AppAnalyticsState): string {
  let eventTypes = ['page'];
  if (params.selectedChannels.length > 0) {
    eventTypes = ['video', 'liveChannel', 'liveChannelWatch'];
  } else if (params.selectedItems.length > 0) {
    eventTypes = ['video'];
  }

  let query = `SELECT 
                ${_getGroupByClause(params)} AS "site",
                DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")) AS "eventdate",
                count(distinct(coalesce("user", 'unauthenticated'))) as "total"
              FROM EVENTS
              WHERE "eventtype" IN ('${eventTypes.join("','")}')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);
  query += `
              GROUP BY ${_getGroupByClause(params)}, 
              DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
              ORDER BY ${_getGroupByClause(params)}, 
              DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;
  return query;
}

function buildVideoViewsQuery(params: AppAnalyticsState): string {
  const eventTypes = ['watch', 'liveChannelWatch'];

  let query = `
          SELECT ${_getGroupByClause(params)} AS "site",
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")) AS "eventdate",
            count(*) AS "total",
            to_number(sum("params": content.duration), 29, 9) AS "duration",
            CASE "total" WHEN 0 THEN 0 ELSE to_number("duration" / "total", 29, 9) END AS "average"
          FROM EVENTS
          WHERE "eventtype" IN ('${eventTypes.join("','")}')
            AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
            AND "site" IN ('${params.sites.join("','")}')`;

  query += _getAdditionalWhereClauses(params);
  query += `
          GROUP BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"))
          ORDER BY ${_getGroupByClause(params)},
            DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate"));`;
  return query;
}

function buildVideoByDeviceQuery(params: AppAnalyticsState): string {
  let query = `SELECT
                ${_getGroupByClause(params)} AS "site",
                CASE
                WHEN "devicetype" IN ('web', 'undefined') THEN 'web'
                WHEN "devicetype" IN ('firetv', 'fire_tv') THEN 'firetv'
                ELSE "devicetype"
                END AS "devicetype",
                'watch' AS "eventtype",
                count(*) AS "views",
                to_number(sum("params":content.duration), 29, 9) AS "duration",
                CASE count(*) WHEN 0 THEN 0 ELSE to_number(sum("params":content.duration) / count(*), 29, 9) END AS "average"
              FROM EVENTS
              WHERE "eventtype" IN ('watch', 'liveChannelWatch')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN (${params.sites.map((site) => `'${site}'`).join(', ')})`;

  query += _getVideoWhereClause(params);
  query += _getChannelWhereClause(params);
  query += `
              GROUP BY 1, 2, 3
              ORDER BY 1, 2;`;
  return query;
}

function buildVideoByCountryQuery(params: AppAnalyticsState): string {
  let query = `SELECT
                ${_getGroupByClause(params)} AS "site",
                "countrycode",
                'watch' AS "eventtype",
                count(*) AS "views",
                to_number(sum("params":content.duration), 29, 9) AS "duration",
                CASE count(*) WHEN 0 THEN 0 ELSE to_number(sum("params":content.duration) / count(*), 29, 9) END AS "average"
              FROM EVENTS
              WHERE "eventtype" IN ('watch', 'liveChannelWatch')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN (${params.sites.map((site) => `'${site}'`).join(', ')})`;

  query += _getVideoWhereClause(params);
  query += _getChannelWhereClause(params);
  query += `
              GROUP BY 1, 2, 3
              ORDER BY 1, 2;`;
  return query;
}

function buildUnifiedAnalyticsQuery(params: AppAnalyticsState): string {
  let query = `SELECT
                "site",
                COALESCE("liveChannel", "channel") AS "channel",
                "video",
                DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")) AS "eventdate",
                "eventtype",
                CASE
                WHEN "devicetype" IN ('web', 'undefined') THEN 'web'
                WHEN "devicetype" IN ('firetv', 'fire_tv') THEN 'firetv'
                ELSE "devicetype"
                END AS "devicetype",
                "countrycode",
                count(*) AS "views",
                count(distinct(coalesce("user", 'unauthenticated'))) AS "uniqueviews",
                to_number(sum("params":content.duration), 29, 9) AS "duration"
              FROM EVENTS
              WHERE "eventtype" IN ('page', 'watch', 'liveChannelWatch')
                AND "eventdate" BETWEEN '${params.fromDate}' AND '${params.toDate}'
                AND "site" IN (${params.sites.map((site) => `'${site}'`).join(', ')})`;

  query += _getVideoWhereClause(params);
  query += _getChannelWhereClause(params);
  query += `
              GROUP BY
                "site",
                COALESCE("liveChannel", "channel"),
                "video",
                DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")),
                "eventtype",
                "devicetype",
                "countrycode"
              ORDER BY
                "site",
                DATE_TRUNC('${params.timePart}', DATEADD(minutes, ${params.offset}, "eventdate")),
                "eventtype";`;
  return query;
}

function _buildQueryByType(type: SnowflakeQueries, params: AppAnalyticsState): string {
  switch (type) {
    case SnowflakeQueries.topChannels:
      return buildTopChannelsQuery(params);
    case SnowflakeQueries.content:
      return buildContentAnalyticsQuery(params);
    case SnowflakeQueries.contentByTop:
      return buildTopContentDataQuery(params);
    case SnowflakeQueries.channel:
      return buildChannelAnalyticsQuery(params);
    case SnowflakeQueries.liveChannel:
      return buildLiveChannelAnalyticsQuery(params);
    case SnowflakeQueries.pageViews:
      return buildPageViewsQuery(params);
    case SnowflakeQueries.uniqueVisitors:
      return buildUniqueVisitorsQuery(params);
    case SnowflakeQueries.videoViews:
      return buildVideoViewsQuery(params);
    case SnowflakeQueries.videoByDevice:
      return buildVideoByDeviceQuery(params);
    case SnowflakeQueries.videoByCountry:
      return buildVideoByCountryQuery(params);
    case SnowflakeQueries.unified:
      return buildUnifiedAnalyticsQuery(params);
    default:
      throw new Error(`No query builder for type: ${type}`);
  }
}

// #endregion

// #region Individual Query Functions

async function _executeSingleQuery(params: AppAnalyticsState, query: string): Promise<BaseAnalyticsData[]> {
  const queryResults = await FuelAnalyticsService.post('analytics/executequery', {
    provider: 'SnowflakeLegacy',
    query,
  });

  if (queryResults?.data?.length > 0) {
    return queryResults.data;
  }

  return [];
}

async function fetchTopChannels(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildTopChannelsQuery(params));
  if (data.length > 0) {
    return data.reverse();
  }
  return [];
}

async function fetchContentAnalytics(params: AppAnalyticsState) {
  return _executeSingleQuery(params, buildContentAnalyticsQuery(params));
}

async function fetchChannelAnalytics(params: AppAnalyticsState) {
  return _executeSingleQuery(params, buildChannelAnalyticsQuery(params));
}

async function fetchLiveChannelAnalytics(params: AppAnalyticsState) {
  return _executeSingleQuery(params, buildLiveChannelAnalyticsQuery(params));
}

async function fetchPageViews(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildPageViewsQuery(params));
  if (data.length > 0) {
    return _correctDates(data, params);
  }
  return [];
}

async function fetchTopContentData(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildTopContentDataQuery(params));
  if (data.length > 0) {
    return _enrichTopContentTitles(data);
  }
  return [];
}

async function fetchUniqueVisitors(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildUniqueVisitorsQuery(params));
  if (data.length > 0) {
    return _correctDates(data, params);
  }
  return [];
}

async function fetchVideoViews(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildVideoViewsQuery(params));
  if (data.length > 0) {
    return _correctDates(data, params);
  }
  return [];
}

async function fetchVideoByDevice(params: AppAnalyticsState) {
  return _executeSingleQuery(params, buildVideoByDeviceQuery(params));
}

async function fetchVideoByCountry(params: AppAnalyticsState) {
  return _executeSingleQuery(params, buildVideoByCountryQuery(params));
}

async function fetchUnifiedAnalytics(params: AppAnalyticsState) {
  const data = await _executeSingleQuery(params, buildUnifiedAnalyticsQuery(params));
  if (data.length > 0) {
    return _correctDates(data, params);
  }
  return [];
}

// #endregion

// #region Batch helpers

function _extractBatchRows(result: AaQueryResult | { data?: BaseAnalyticsData[] } | undefined): BaseAnalyticsData[] {
  if (!result) {
    return [];
  }

  if ('queryExecutionStatus' in result && result.queryExecutionStatus && result.queryExecutionStatus !== 'Succeeded') {
    // eslint-disable-next-line no-console -- batch diagnostic
    console.warn(`[AppAnalytics] batch query failed: ${result.queryExecutionStatus}`, result.message);
    return [];
  }

  if (result.data?.length > 0) {
    return result.data;
  }

  return [];
}

async function _postProcessBatchRows(
  key: SnowflakeQueries,
  rows: BaseAnalyticsData[],
  params: AppAnalyticsState
): Promise<BaseAnalyticsData[]> {
  if (rows.length === 0) {
    return [];
  }

  switch (key) {
    case SnowflakeQueries.pageViews:
    case SnowflakeQueries.uniqueVisitors:
    case SnowflakeQueries.videoViews:
      return _correctDates([...rows], params);
    case SnowflakeQueries.topChannels:
      return [...rows].reverse();
    case SnowflakeQueries.contentByTop:
      return _enrichTopContentTitles([...rows]);
    default:
      return rows;
  }
}

async function _enrichTopContentTitles(data: BaseAnalyticsData[]): Promise<BaseAnalyticsData[]> {
  const missingTitleVideoIds = Array.from(
    new Set(
      data
        .filter((row) => clientUtils.isNullOrWhitespace(row.title) && typeof row.video === 'string' && clientUtils.isGuid(row.video))
        .map((row) => row.video as string)
    )
  );

  if (missingTitleVideoIds.length === 0) {
    return data;
  }

  const clips = await FuelPortalService.batchGet<Item[]>(FuelEntityType.Clip, missingTitleVideoIds);
  const titleByClipId = new Map<string, string>();

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    if (typeof clip?.id === 'string' && !clientUtils.isNullOrWhitespace(clip.title)) {
      titleByClipId.set(clip.id, clip.title);
    }
  }

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (clientUtils.isNullOrWhitespace(row.title) && typeof row.video === 'string') {
      const resolvedTitle = titleByClipId.get(row.video);
      if (!clientUtils.isNullOrWhitespace(resolvedTitle)) {
        row.title = resolvedTitle;
      }
    }
  }

  return data;
}

// #endregion

// #region Helper Functions

function _retryInflightRequest(type: SnowflakeQueries, params: AppAnalyticsState, delay: number) {
  return new Promise((resolve) => {
    setTimeout(async () => {
      const service = AppAnalyticsSnowflakeService();
      const data = await service.getData(type, params);
      resolve(data);
    }, delay);
  });
}

function _correctDates(data: BaseAnalyticsData[], params: AppAnalyticsState) {
  for (let i = 0; i < data.length; i++) {
    data[i].eventdate = DateTime.fromISO(`${data[i].eventdate}.000Z`)
      .minus({ minutes: params.offset })
      .toUTC()
      .toISO();
  }
  return data;
}

function _getAdditionalWhereClauses(params: AppAnalyticsState) {
  let additionalWhereClauses = '';
  additionalWhereClauses += _getVideoWhereClause(params);
  additionalWhereClauses += _getChannelWhereClause(params);
  return additionalWhereClauses;
}

function _getChannelWhereClause(params: AppAnalyticsState) {
  if (params.selectedChannels.length > 0) {
    return `
          AND ("channel" IN ('${params.selectedChannels.map((x) => x.id).join("','")}','${params.selectedChannels
        .filter((x) => !clientUtils.isNullOrWhitespace(x.alias))
        .map((x) => x.alias)
        .join("','")}')
            OR "liveChannel" IN ('${params.selectedChannels.map((x) => x.id).join("','")}','${params.selectedChannels
        .filter((x) => !clientUtils.isNullOrWhitespace(x.alias))
        .map((x) => x.alias)
        .join("','")}'))`;
  }
  return '';
}

function _getGroupByClause(params: AppAnalyticsState) {
  if (params.groupBy === 'channel') {
    return 'COALESCE("liveChannel", "channel")';
  }
  if (params.groupBy === 'category') {
    return '"video"';
  }
  return `"${params.groupBy}"`;
}

function _getVideoWhereClause(params: AppAnalyticsState) {
  if (params.selectedItems.length > 0) {
    return `
          AND "video" IN ('${params.selectedItems.map((x) => x.id).join("','")}')`;
  }
  if (params.categoryVideoIds?.length > 0) {
    return `
          AND "video" IN ('${params.categoryVideoIds.join("','")}')`;
  }
  return '';
}

function _composeKey(prefix: string, params: AppAnalyticsState): string {
  let key = `${prefix}_`;
  key += `FROM:${params.fromDate}_TO:${params.toDate}_SITES:${params.sites.sort().join(',')}`;
  key += `_CLIPS:${params.selectedItems.map((x) => x.id).join(',')}`;
  key += `_CHANNELS:${params.selectedChannels.map((x) => x.id).join(',')}`;
  key += `_CATVIDS:${(params.categoryVideoIds ?? []).sort().join(',')}`;
  key += `_GROUPBY:${params.groupBy}`;
  key += `_TIMEPART:${params.timePart}`;
  return key;
}

// #endregion
